import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  InputAdornment,
  Grid,
  Pagination,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import './AllOrdersPage.css';

const AllOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [downloading, setDownloading] = useState(false);
  

  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dealerFilter, setDealerFilter] = useState('');
  const [dealers, setDealers] = useState([]);
  

  const [sortBy, setSortBy] = useState('date-desc');
  
  const API_BASE_URL = 'https://test2.planfix-to-syrve.com';
  
  useEffect(() => {
    fetchAllOrders();
  }, []);
  
  useEffect(() => {
    applyFiltersAndSorting();
  }, [orders, searchTerm, dateFrom, dateTo, dealerFilter, sortBy]);
  
  const fetchAllOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/api/all-orders`);
      if (response.data.success) {
     
        const ordersWithKeys = response.data.data.map((order, index) => {
          return {
            ...order,
            _uniqueId: `${order.id || order.orderNumber}_${order.userId || 'unknown'}_${index}`
          };
        });
        
        setOrders(ordersWithKeys);
        const uniqueDealers = [...new Set(ordersWithKeys.map(order => order.dealer).filter(d => d))];
        setDealers(uniqueDealers);
      } else {
        setError('Помилка завантаження замовлень');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Помилка при завантаженні замовлень');
    } finally {
      setLoading(false);
    }
  };
  
  const applyFiltersAndSorting = () => {
    let filtered = [...orders];
    

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order => 
        order.orderNumber?.toLowerCase().includes(term) ||
        order.dealer?.toLowerCase().includes(term)
      );
    }
    

    if (dealerFilter) {
      filtered = filtered.filter(order => order.dealer === dealerFilter);
    }
    
  
    if (dateFrom) {
      filtered = filtered.filter(order => {
        const orderDate = order.date || '';
        return orderDate >= dateFrom;
      });
    }
    

    if (dateTo) {
      filtered = filtered.filter(order => {
        const orderDate = order.date || '';
        return orderDate <= dateTo;
      });
    }
    
   
    filtered = applySorting(filtered);
    
    setFilteredOrders(filtered);
  };
  
  const applySorting = (ordersList) => {
    const sorted = [...ordersList];
    
    switch (sortBy) {
      case 'date-desc':
        sorted.sort((a, b) => {
          const dateA = new Date(a.dateTime || a.date || 0);
          const dateB = new Date(b.dateTime || b.date || 0);
          return dateB - dateA;
        });
        break;
      case 'date-asc':
        sorted.sort((a, b) => {
          const dateA = new Date(a.dateTime || a.date || 0);
          const dateB = new Date(b.dateTime || b.date || 0);
          return dateA - dateB;
        });
        break;
      case 'sum-desc':
        sorted.sort((a, b) => {
          const sumA = a.items?.reduce((sum, item) => {
            const price = item.finalPrice || item.price || 0;
            return sum + (price * (item.quantity || 1));
          }, 0) || 0;
          const sumB = b.items?.reduce((sum, item) => {
            const price = item.finalPrice || item.price || 0;
            return sum + (price * (item.quantity || 1));
          }, 0) || 0;
          return sumB - sumA;
        });
        break;
      case 'sum-asc':
        sorted.sort((a, b) => {
          const sumA = a.items?.reduce((sum, item) => {
            const price = item.finalPrice || item.price || 0;
            return sum + (price * (item.quantity || 1));
          }, 0) || 0;
          const sumB = b.items?.reduce((sum, item) => {
            const price = item.finalPrice || item.price || 0;
            return sum + (price * (item.quantity || 1));
          }, 0) || 0;
          return sumA - sumB;
        });
        break;
      case 'items-desc':
        sorted.sort((a, b) => (b.items?.length || 0) - (a.items?.length || 0));
        break;
      case 'items-asc':
        sorted.sort((a, b) => (a.items?.length || 0) - (b.items?.length || 0));
        break;
      default:
        break;
    }
    
    return sorted;
  };
  
  const clearFilters = () => {
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    setDealerFilter('');
  };
  
  const viewOrder = (order) => {
    setSelectedOrder(order);
    setExpandedItems({});
    setShowOrderDetails(true);
  };
  
  const closeOrderDetails = () => {
    setShowOrderDetails(false);
    setSelectedOrder(null);
    setExpandedItems({});
  };
  
  const toggleItemExpand = (itemIndex) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemIndex]: !prev[itemIndex]
    }));
  };
  
  const downloadOrderExcel = async (order) => {
    if (downloading) return;
    
    try {
      setDownloading(true);
      console.log('Завантаження Excel файлів для замовлення:', order.orderNumber);
      
      const response = await axios({
        method: 'GET',
        url: `${API_BASE_URL}/api/download-order-excel/${order.userId}/${order.id}`,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${order.orderNumber}_${order.dealer || 'order'}_files.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log('Файли успішно завантажено');
    } catch (error) {
      console.error('Помилка завантаження Excel файлів:', error);
      alert('Помилка при завантаженні файлів. Спробуйте пізніше.');
    } finally {
      setDownloading(false);
    }
  };
  

  const stats = useMemo(() => {
    return {
      total: orders.length,
      totalSum: orders.reduce((sum, order) => {
        const orderSum = order.items?.reduce((orderSum, item) => {
          const price = item.finalPrice || item.price || 0;
          return orderSum + (price * (item.quantity || 1));
        }, 0) || 0;
        return sum + orderSum;
      }, 0)
    };
  }, [orders]);
  

  const getSortLabel = (value) => {
    const labels = {
      'date-desc': 'Дата (нові → старі)',
      'date-asc': 'Дата (старі → нові)',
      'sum-desc': 'Вартість (від більшої)',
      'sum-asc': 'Вартість (від меншої)',
      'items-desc': 'Позицій (більше)',
      'items-asc': 'Позицій (менше)'
    };
    return labels[value] || 'Сортувати';
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" sx={{ mt: 8 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Завантаження замовлень...</Typography>
      </Box>
    );
  }
  
  return (
    <div className="all-orders-page">
      <div className="orders-list-modal">
   
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Пошук за номером або дилером</label>
              <div className="filter-input-wrapper">
                <SearchIcon className="filter-icon" />
                <input
                  type="text"
                  placeholder="Наприклад: ЗАМ-001"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="filter-clear-btn" onClick={() => setSearchTerm('')}>✕</button>
                )}
              </div>
            </div>
            <div className="filter-group">
              <label>Дилер</label>
              <select value={dealerFilter} onChange={(e) => setDealerFilter(e.target.value)}>
                <option value="">Всі дилери</option>
                {dealers.map(dealer => (
                  <option key={dealer} value={dealer}>{dealer}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Дата від</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="filter-group">
              <label>Дата до</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div className="filter-group filter-actions">
              <button className="clear-filters-btn" onClick={clearFilters}>
                <ClearIcon /> Очистити фільтри
              </button>
            </div>
          </div>
        </div>

       
        <div className="sorting-panel">
          <div className="sorting-group">
            <label>Сортувати за:</label>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="date-desc">Дата (нові → старі)</option>
              <option value="date-asc">Дата (старі → нові)</option>
              <option value="sum-desc">Вартість (від більшої)</option>
              <option value="sum-asc">Вартість (від меншої)</option>
              <option value="items-desc">Позицій (більше)</option>
              <option value="items-asc">Позицій (менше)</option>
            </select>
          </div>
          <div className="sorting-info">
            <span className="filtered-count">
              Знайдено: <strong>{filteredOrders.length}</strong> замовлень
            </span>
          </div>
        </div>


        {filteredOrders.length === 0 ? (
          <p className="no-orders">Замовлення не знайдено</p>
        ) : (
          <div className="orders-grid all-orders-grid">
            {filteredOrders.map(order => {
              const orderSum = order.items?.reduce((sum, item) => {
                const price = item.finalPrice || item.price || 0;
                return sum + (price * (item.quantity || 1));
              }, 0) || 0;
              
       
              const uniqueKey = order._uniqueId || `${order.id || order.orderNumber}_${order.userId || 'unknown'}`;
              
              return (
                <div key={uniqueKey} className={`order-card ${order.status}`}>
                  <div className="order-card-header">
                    <span className="order-number">{order.orderNumber}</span>
                    <span className={`order-status ${order.status}`}>
                      {order.status === 'draft' ? 'Чернетка' : 
                       order.status === 'confirmed' ? 'Підтверджено' : 'Архів'}
                    </span>
                  </div>
                  <div className="order-card-body" style={{ textAlign: 'left' }}>
                    <div>Дата: {order.date ? new Date(order.date).toLocaleDateString('uk-UA') : 'Немає дати'}</div>
                    <div>Час: {order.time || 'Немає часу'}</div>
                    <div>Дилер: <strong>{order.dealer || 'Не вказано'}</strong></div>
                    <div>Позицій: {order.items?.length || 0}</div>
                    <div>Сума: {orderSum.toFixed(2)} €</div>
                  </div>
                  <div className="order-card-actions">
                    <button onClick={() => viewOrder(order)}>Відкрити</button>
                    <button 
                      onClick={() => downloadOrderExcel(order)}
                      disabled={downloading}
                      className="download-excel-btn"
                    >
                      <DownloadIcon /> 
                      {downloading ? '...' : ''}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
      
        {filteredOrders.length > 0 && (
          <div className="orders-count-info">
            <span>Показано всі {filteredOrders.length} замовлень</span>
            <span className="sort-info">
              Сортування: {getSortLabel(sortBy)}
            </span>
          </div>
        )}
      </div>


      {showOrderDetails && selectedOrder && (
        <div className="confirm-dialog-overlay" onClick={closeOrderDetails}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-dialog-header">
              <h3>Деталі замовлення {selectedOrder.orderNumber}</h3>
              <button className="confirm-dialog-close" onClick={closeOrderDetails}>
                <CloseIcon />
              </button>
            </div>
            <div className="confirm-dialog-message">
      
              <div className="order-info-section">
                <div className="order-info-row">
                  <span className="info-label">Дилер:</span>
                  <span className="info-value">{selectedOrder.dealer || '—'}</span>
                </div>
                <div className="order-info-row">
                  <span className="info-label">Дата:</span>
                  <span className="info-value">
                    {selectedOrder.date ? new Date(selectedOrder.date).toLocaleDateString('uk-UA') : 'Немає дати'}
                    {selectedOrder.time && ` ${selectedOrder.time}`}
                  </span>
                </div>
                <div className="order-info-row">
                  <span className="info-label">Статус:</span>
                  <span className={`status-badge ${selectedOrder.status}`}>
                    {selectedOrder.status === 'draft' ? 'Чернетка' : 
                     selectedOrder.status === 'confirmed' ? 'Підтверджено' : 'Архів'}
                  </span>
                </div>
                <div className="order-info-row">
                  <span className="info-label">Excel файли:</span>
                  <button 
                    onClick={() => downloadOrderExcel(selectedOrder)}
                    disabled={downloading}
                    className="download-excel-btn-small"
                  >
                    <DownloadIcon /> 
                    {downloading ? '...' : ''}
                  </button>
                </div>
              </div>
              
       
              <div className="order-items-section">
                <h4>Позиції замовлення:</h4>
                <div className="items-scroll-container">
                  {selectedOrder.items?.map((item, idx) => {
             
                    let accessoriesTotal = 0;
                    let accessoriesList = [];
                    
                    if (item.add && Array.isArray(item.add) && item.add.length > 0) {
                      accessoriesList = item.add.map((acc, accIdx) => {
                        const price = parseFloat(acc.basePriceadd) || parseFloat(acc.price) || 0;
                        const quantity = parseInt(acc.amountadd) || parseInt(acc.quantity) || 1;
                        accessoriesTotal += price * quantity;
                        return {
                          id: accIdx,
                          name: acc.name_add || acc.name,
                          quantity: quantity,
                          price: price,
                          sum: price * quantity
                        };
                      });
                    } else if (item.accessoriesItems && item.accessoriesItems.length > 0) {
                      accessoriesList = item.accessoriesItems.map((acc, accIdx) => {
                        const price = parseFloat(acc.price) || 0;
                        const quantity = parseInt(acc.quantity) || 1;
                        accessoriesTotal += price * quantity;
                        return {
                          id: accIdx,
                          name: acc.name,
                          quantity: quantity,
                          price: price,
                          sum: price * quantity
                        };
                      });
                    }
                    
                    const systemPrice = item.finalPrice || item.price || 0;
                    const itemSum = (systemPrice * (item.quantity || 1)) + accessoriesTotal;
                    const isExpanded = expandedItems[idx];
                    
                    return (
                      <div key={idx} className="order-item-detail-card">
                        <div className="order-item-header" onClick={() => toggleItemExpand(idx)}>
                          <div className="order-item-title">
                            <span className="item-number">#{idx + 1}</span>
                            <span className="item-system">
                              {item.systemName || 'Не вказано'}
                              {item.fabricColor && <span className="item-color">, {item.fabricColor}</span>}
                            </span>
                          </div>
                          <div className="order-item-summary">
                            <span className="item-quantity">{item.quantity || 1} шт.</span>
                            <span className="item-sum">{itemSum.toFixed(2)} €</span>
                            <button className="expand-btn">
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </button>
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="order-item-details">
                            <div className="detail-row">
                              <span className="detail-label">Код тканини:</span>
                              <span className="detail-value">{item.fabricCode || '-'}</span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Розміри:</span>
                              <span className="detail-value">{item.width || '-'} x {item.height || '-'} см</span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Сторона управління:</span>
                              <span className="detail-value">
                                {item.controlSide === 'left' ? 'Ліва' : 
                                 item.controlSide === 'right' ? 'Права' : '-'}
                              </span>
                            </div>
                            {item.mountingType && (
                              <div className="detail-row">
                                <span className="detail-label">Тип монтажу:</span>
                                <span className="detail-value">
                                  {item.mountingType === 'wall' ? 'На стіну' :
                                   item.mountingType === 'ceiling' ? 'На стелю' :
                                   item.mountingType === 'opening' ? 'У відкоси' : item.mountingType}
                                </span>
                              </div>
                            )}
                            <div className="detail-row">
                              <span className="detail-label">Ціна за од.:</span>
                              <span className="detail-value">{systemPrice.toFixed(2)} €</span>
                            </div>
                            
                       
                            {accessoriesList.length > 0 && (
                              <div className="accessories-section">
                                <div className="accessories-title">Додаткові комплектуючі:</div>
                                {accessoriesList.map((acc, accIdx) => (
                                  <div key={accIdx} className="accessory-row">
                                    <span className="accessory-name-m">{acc.name}</span>
                                    <span className="accessory-quantity-m">{acc.quantity} шт.</span>
                                    <span className="accessory-sum">{acc.sum.toFixed(2)} €</span>
                                  </div>
                                ))}
                                <div className="accessories-total">
                                  <span>Вартість додатків:</span>
                                  <span>{accessoriesTotal.toFixed(2)} €</span>
                                </div>
                              </div>
                            )}
                            
                            <div className="detail-row total-row">
                              <span className="detail-label">Сума позиції:</span>
                              <span className="detail-value total-value">{itemSum.toFixed(2)} €</span>
                            </div>
                            
                            {item.itemNotes && (
                              <div className="item-notes-section">
                                <span className="notes-label">Примітки:</span>
                                <span className="notes-value">{item.itemNotes}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
    
              <div className="order-total-section">
                <div className="total-row final">
                  <span>Загальна сума замовлення:</span>
                  <span>
                    {selectedOrder.items?.reduce((sum, item) => {
                      let accessoriesTotal = 0;
                      if (item.add && Array.isArray(item.add)) {
                        accessoriesTotal = item.add.reduce((accSum, acc) => 
                          accSum + (parseFloat(acc.basePriceadd) || parseFloat(acc.price) || 0) * 
                          (parseInt(acc.amountadd) || parseInt(acc.quantity) || 1), 0);
                      } else if (item.accessoriesTotal) {
                        accessoriesTotal = item.accessoriesTotal;
                      }
                      const itemPrice = item.finalPrice || item.price || 0;
                      return sum + (itemPrice * (item.quantity || 1)) + accessoriesTotal;
                    }, 0).toFixed(2)} €
                  </span>
                </div>
              </div>
              
        
              {selectedOrder.notes && (
                <div className="order-notes-section">
                  <div className="notes-label">Примітки до замовлення:</div>
                  <div className="notes-value">{selectedOrder.notes}</div>
                </div>
              )}
            </div>
            <div className="confirm-dialog-buttons">
              <button onClick={closeOrderDetails} className="cancel-btn">Закрити</button>
              <button 
                onClick={() => downloadOrderExcel(selectedOrder)}
                disabled={downloading}
                className="download-btn"
              >
                <DownloadIcon /> 
                {downloading ? 'Завантаження...' : 'Завантажити Excel файли'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllOrdersPage;
